import React, { useEffect, useState } from "react";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import ActionPlanApi from "../../services/ActionPlanApi";
import OperationApi from "../../services/OperationApi";
import PageHeader from "../../components/PageHeader/PageHeader";
import FormCard from "../../components/FormCard/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const CreateActionPlan: React.FC = () => {
  const { failureId, fmeaId } = useParams<{
    failureId?: string;
    fmeaId?: string;
  }>();

  const { displayToast } = useToast();
  const navigate = useNavigate();
  const { t: translate } = useTranslation("form");

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [operationName, setOperationName] = useState<string>("");
  const [operationId, setOperationId] = useState<string | null>(null);
  const [actionType, setActionType] = useState<string>("");
  const [status] = useState<string>("Planned");
  const [forecastDate, setForecastDate] = useState<Date | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responsibleSector, setResponsibleSector] = useState<string>("");
  const [responsiblePerson, setResponsiblePerson] = useState<string>("");

  const [error, setError] = useState<boolean>(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  useEffect(() => {
    const loadOperations = async (): Promise<void> => {
      try {
        const res = await OperationApi.GetByFailureId(failureId ?? "");
        const ops = res.data
          ? [{ label: res.data.name, value: res.data.id }]
          : [];

        if (ops.length === 1) {
          setOperationId(ops[0].value);
          setOperationName(ops[0].label);
        }
      } catch (error) {
        displayToast(
          "error",
          translate("error"),
          translate("errorFetchingData"),
          false,
          3000,
        );
      }
    };
    loadOperations();
  }, [displayToast, translate, failureId]);

  const handleCancel = (): void => {
    setShowConfirmCancel(true);
  };

  const handleSave = async (): Promise<void> => {
    if (
      !name.trim() ||
      !description.trim() ||
      !operationId ||
      !actionType.trim() ||
      !forecastDate ||
      !responsibleSector.trim() ||
      !responsiblePerson.trim()
    ) {
      setError(true);
      displayToast(
        "warn",
        translate("warning"),
        translate("fillInAllRequiredFields"),
        false,
        3000,
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        failureId: failureId as string,
        name,
        description,
        type: actionType,
        status,
        forecastDate,
        responsibleSector,
        responsiblePerson,
      };
      await ActionPlanApi.Create(payload);
      displayToast(
        "success",
        translate("success"),
        translate("actionPlanCreatedSuccess"),
        false,
        3000,
      );
      navigate(`/fmeas/${fmeaId}/failures/${failureId}/action-plans`);
    } catch (error: any) {
      const apiErrors = error?.data?.data?.error;

      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        const messages = apiErrors
          .map((err: any) => `• ${err.errorMessage}`)
          .join("\n");
        displayToast(
          "error",
          translate("actionPlanCreatedFailed"),
          messages,
          false,
          5000,
        );
      } else {
        const fallbackMsg =
          error?.data?.data?.message ?? translate("actionPlanCreatedFailed");
        displayToast(
          "error",
          translate("actionPlanCreatedFailed"),
          fallbackMsg,
          false,
          5000,
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <PageHeader 
        title={translate("createActionPlan")}
        description={translate("fillTheFormToCreateActionPlan") || "Preencha os campos abaixo para registrar um novo plano de ação."}
      />

      <FormCard
        title={translate("generalData") || "Dados Gerais"}
        icon="assignment"
        numColumns={2}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitButtonLabel={translate("register") || "Cadastrar"}
        cancelButtonLabel={translate("cancel") || "Cancelar"}
        disabled={isSubmitting}
      >
        <div className="col-12">
          <Input
            type="text"
            label={translate("name")}
            required
            value={name}
            onChange={(val) => {
              setName(val);
              setError(false);
            }}
            placeholder={`${translate("name")} *`}
            maxLength={50}
            className={error && !name.trim() ? "p-invalid" : ""}
          />
        </div>

        <div className="col-12">
          <Input
            type="textArea"
            label={translate("description")}
            required
            value={description}
            onChange={(val) => {
              setDescription(val);
              setError(false);
            }}
            placeholder={`${translate("description")} *`}
            maxLength={600}
            rows={3}
            className={error && !description.trim() ? "p-invalid" : ""}
          />
        </div>

        <Input
          type="text"
          label={translate("operation")}
          required
          value={operationName}
          onChange={() => {}}
          disabled
          placeholder={`${translate("operation")} *`}
        />

        <Input
          type="text"
          label={translate("actionType")}
          required
          value={actionType}
          onChange={(val) => {
            setActionType(val);
            setError(false);
          }}
          placeholder={`${translate("actionType")} *`}
          maxLength={50}
          className={error && !actionType.trim() ? "p-invalid" : ""}
        />

        <Input
          type="calendar"
          label={translate("forecastDate")}
          required
          value={forecastDate}
          onChange={(val) => {
            setForecastDate(val);
            setError(false);
          }}
        />

        <Input
          type="text"
          label={translate("responsibleSector")}
          required
          value={responsibleSector}
          onChange={(val) => {
            setResponsibleSector(val);
            setError(false);
          }}
          placeholder={`${translate("responsibleSector")} *`}
          maxLength={50}
          className={error && !responsibleSector.trim() ? "p-invalid" : ""}
        />

        <Input
          type="text"
          label={translate("responsiblePerson")}
          required
          value={responsiblePerson}
          onChange={(val) => {
            setResponsiblePerson(val);
            setError(false);
          }}
          placeholder={`${translate("responsiblePerson")} *`}
          maxLength={50}
          className={error && !responsiblePerson.trim() ? "p-invalid" : ""}
        />

        <Input
          type="text"
          label={translate("status")}
          required
          value={translate(`ActionPlanStatus${status}`)}
          onChange={() => {}}
          disabled
        />
      </FormCard>
      
      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate(`/fmeas/${fmeaId}/failures/${failureId}/action-plans`)}
      />
    </div>
  );
};

export default CreateActionPlan;
